/**
 * Motion of the demonstration ("De la tâche au territoire").
 *
 * The two visual timelines are built once and used two ways: nested in the pinned
 * scene and scrubbed by the scroll (desktop), or played once on enter (stacked
 * layout). Every tween is a fromTo, so invalidating on refresh is always safe (the day
 * threads also repaint from their proxies, see repaintOf), and the default markup of
 * each visual is its final state (reduced motion shows it as is).
 */
import { gsap, SplitText, EASE } from '../../lib/gsap';
import { setThread } from './threads';

/**
 * The pinned scene, in timeline units. The scene pins for SCENE.pin viewport heights of
 * scroll; the timeline starts SCENE.lead viewport heights earlier, while the scene slides
 * in, so the agent is already at work when the stage locks into place. The whole
 * timeline is mapped onto that distance (lead + pin).
 *
 *   0.2   agent run (steps check in, facts, draft, approval), until about 4.5
 *   5.4   camera 1 > 2: the task shrinks into its block of the day
 *   7.2   the day shifts: chores flow into the lane, gold fills the freed slots
 *   10.7  camera 2 > 3: the day shrinks into the agents node, the territory zooms out
 *   11.9  the territory flows: sources, agents, signals, human validation (about 4.4)
 *   16.8  end
 */
export const SCENE = {
	pin: 3.7,
	lead: 0.45,
	length: 16.8,
	agentAt: 0.2,
	morphAt: 5.4,
	morphLength: 1.5,
	dayAt: 7.2,
	zoomAt: 10.7,
	zoomLength: 1.6,
	territoryAt: 11.9,
};

const unit = (time: number) => time / SCENE.length;

/** Progress of the scene timeline where the left column switches to the next beat. */
export const SWITCH = [
	unit(SCENE.morphAt + SCENE.morphLength * 0.5),
	unit(SCENE.zoomAt + SCENE.zoomLength * 0.5),
];

/** Progress of the scene timeline where each beat rests, composed (step buttons and focus land here). */
export const REST = [unit(SCENE.morphAt - 0.3), unit(SCENE.zoomAt - 0.3), unit(SCENE.length - 0.4)];

export type Box = { x: number; y: number; w: number; h: number };

/**
 * Layout box of `el` in the coordinate space of `root`, from offsets: transforms are
 * ignored, so it can be measured while the scene is mid-animation.
 */
export function boxIn(el: Element | undefined, root: Element | undefined): Box {
	if (!(el instanceof HTMLElement) || !(root instanceof HTMLElement)) return { x: 0, y: 0, w: 0, h: 0 };
	let x = 0;
	let y = 0;
	let node: HTMLElement | null = el;
	while (node && node !== root) {
		x += node.offsetLeft;
		y += node.offsetTop;
		node = node.offsetParent as HTMLElement | null;
	}
	return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}

/**
 * "Une tâche": from a fresh request to "waiting for your validation".
 * Steps check in one after another; while extracting, each value is marked in the
 * request and appears in the record; the draft appears; then the approval. The
 * approval only takes pointer events once it is shown. About 4.3 units long.
 */
export function agentRun(root: Element): gsap.core.Timeline {
	const q = gsap.utils.selector(root);
	const steps = q('[data-agent-step]');
	const marks = q('[data-agent-mark]');
	const facts = q('[data-agent-fact]');
	const draft = q('[data-agent-draft]');
	const approval = q('[data-agent-approval]');
	const unread = q('[data-agent-unread]');
	const tl = gsap.timeline({ defaults: { ease: EASE } });
	if (!steps.length) return tl;

	const last = steps.length - 1;
	const factsAt = Math.min(1, last);
	const draftAt = Math.max(0, last - 1);
	if (approval.length) gsap.set(approval, { pointerEvents: 'none' });

	let t = 0;
	steps.forEach((step, index) => {
		const check = step.querySelector('[data-agent-check]');
		const waiting = step.querySelector('[data-agent-await]');
		const link = step.querySelector('[data-agent-link]');
		let checkAt = t + 0.4;

		tl.fromTo(step, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5 }, t);

		if (index === 0 && unread.length) {
			tl.fromTo(unread, { opacity: 1, scale: 1 }, { opacity: 0, scale: 0.3, duration: 0.3, ease: 'power2.in' }, t + 0.25);
		}
		if (index === factsAt && index !== last && (marks.length || facts.length)) {
			marks.forEach((mark, i) => {
				tl.fromTo(mark, { scaleX: 0 }, { scaleX: 1, duration: 0.4, ease: 'power2.out' }, t + 0.3 + i * 0.24);
			});
			facts.forEach((fact, i) => {
				tl.fromTo(fact, { opacity: 0, x: -8 }, { opacity: 1, x: 0, duration: 0.45 }, t + 0.42 + i * 0.24);
			});
			checkAt = t + 0.42 + Math.max(marks.length, facts.length) * 0.24 + 0.2;
		} else if (index === draftAt && index !== last && draft.length) {
			tl.fromTo(draft, { opacity: 0, y: 8, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.5 }, t + 0.3);
			checkAt = t + 0.65;
		}

		if (check) {
			tl.fromTo(check, { opacity: 0, scale: 0.4 }, { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2.2)' }, checkAt);
		}
		if (link) {
			tl.fromTo(link, { scaleY: 0 }, { scaleY: 1, duration: 0.35, ease: 'power1.inOut' }, checkAt + 0.2);
		}
		if (waiting) {
			tl.fromTo(waiting, { opacity: 0, scale: 0.5 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2)' }, t + 0.2);
		}
		if (index === last && approval.length) {
			tl.fromTo(approval, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5 }, t + 0.35);
			tl.set(approval, { pointerEvents: 'auto' }, t + 0.45);
		}
		t = checkAt + 0.35;
	});

	return tl;
}

/** Repaints threads from their proxies (stored on dayShift's and territoryFlow's timelines as data.repaint). */
export type Repaint = () => void;

/** The repaint function attached to a dayShift or territoryFlow timeline, if any. */
export function repaintOf(timeline: gsap.core.Timeline): Repaint | undefined {
	const data = timeline.data as { repaint?: Repaint } | undefined;
	return data?.repaint;
}

/**
 * "Une équipe": the chores leave the day one after another and flow as threads into
 * the automation lane (a solar tip rides each drawing front and rests at the end of
 * the day); the freed slots fill with gold. The lane and its label are there from
 * the start, empty. About 3 units long.
 */
export function dayShift(root: Element): gsap.core.Timeline {
	const q = gsap.utils.selector(root);
	const chores = q('[data-day-chore]');
	const threads = Array.from(root.querySelectorAll<SVGPathElement>('[data-day-thread]'));
	const gold = q('[data-day-returned]');
	const returned = q('[data-day-legend="returned"]');
	const tl = gsap.timeline({ defaults: { ease: EASE } });
	const stagger = 0.3;

	// Each thread is drawn through a proxy (its head position). The tween paints it while
	// it plays; repaint() paints every thread from its proxy, for renders that run with
	// callbacks suppressed (ScrollTrigger positioning the scene on load or refresh).
	const drawn: Array<{ thread: SVGPathElement; state: { b: number } }> = [];
	chores.forEach((chore, index) => {
		const at = index * stagger;
		tl.fromTo(chore, { opacity: 1, x: 0 }, { opacity: 0, x: 16, duration: 0.55, ease: 'power2.in' }, at + 0.1);

		const thread = threads[index];
		if (!thread) return;
		const state = { b: 0 };
		drawn.push({ thread, state });
		const paint = () => setThread(thread, 0, state.b);
		tl.fromTo(state, { b: 0 }, { b: 1, duration: 0.9, ease: 'power2.inOut', onUpdate: paint }, at);
		paint();
	});
	const repaint: Repaint = () => drawn.forEach(({ thread, state }) => setThread(thread, 0, state.b));
	tl.data = { repaint };

	const after = Math.max(0, chores.length - 1) * stagger + 0.9;
	// One tween per block: a staggered fromTo nested in a timeline only pre-renders its first target.
	gold.forEach((block, index) => {
		tl.fromTo(block, { opacity: 0, scale: 0.94 }, { opacity: 1, scale: 1, duration: 0.6 }, after + index * 0.16);
	});
	if (returned.length) tl.fromTo(returned, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.5 }, after + 0.2);

	return tl;
}

/**
 * "Un territoire": the data of the territory flows into the agents (the sources appear,
 * their threads converge), the agents take it in (one solar ring), emit their signals
 * (threads with solar tips, then the signal cards), and every signal reaches the
 * human validation, which checks. The agents node stays visible throughout (the
 * camera lands on it). About 4.4 units long.
 */
export function territoryFlow(root: Element): gsap.core.Timeline {
	const q = gsap.utils.selector(root);
	const sources = q('[data-territory-source]');
	const signals = q('[data-territory-signal]');
	const pulse = q('[data-territory-pulse]');
	const human = q('[data-territory-human]');
	const check = q('[data-territory-check]');
	const ring = q('[data-territory-ring]');
	const threadsOf = (kind: string) =>
		Array.from(root.querySelectorAll<SVGPathElement>(`[data-territory-thread="${kind}"]`));
	const tl = gsap.timeline({ defaults: { ease: EASE } });

	// Threads are drawn through proxies, as in dayShift (repaint for suppressed renders).
	const drawn: Array<{ thread: SVGPathElement; state: { b: number } }> = [];
	const draw = (thread: SVGPathElement, at: number, duration: number) => {
		const state = { b: 0 };
		drawn.push({ thread, state });
		const paint = () => setThread(thread, 0, state.b);
		tl.fromTo(state, { b: 0 }, { b: 1, duration, ease: 'power2.inOut', onUpdate: paint }, at);
		paint();
	};

	// 1. The data: one source after another, its thread runs to the agents.
	sources.forEach((source, i) => {
		tl.fromTo(source, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.5 }, i * 0.1);
	});
	threadsOf('in').forEach((thread, i) => draw(thread, 0.25 + i * 0.1, 0.8));

	// 2. The agents take it in.
	const intake = 0.25 + Math.max(0, sources.length - 1) * 0.1 + 0.75;
	if (pulse.length) {
		tl.fromTo(pulse, { opacity: 0.9, scale: 1 }, { opacity: 0, scale: 1.12, duration: 0.8, ease: 'power2.out' }, intake);
	}

	// 3. They emit the signals.
	const emit = intake + 0.25;
	threadsOf('out').forEach((thread, i) => draw(thread, emit + i * 0.15, 0.75));
	signals.forEach((signal, i) => {
		tl.fromTo(signal, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.5 }, emit + 0.45 + i * 0.15);
	});

	// 4. Every signal reaches the people who decide; the validation checks.
	const decide = emit + 0.45 + Math.max(0, signals.length - 1) * 0.15 + 0.35;
	threadsOf('up').forEach((thread, i) => draw(thread, decide + i * 0.08, 0.7));
	const validated = decide + 0.55;
	if (human.length) tl.fromTo(human, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5 }, validated - 0.2);
	if (check.length) {
		tl.fromTo(check, { opacity: 0, scale: 0.4 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2.2)' }, validated + 0.2);
	}
	if (ring.length) {
		tl.fromTo(ring, { opacity: 0.9, scale: 1 }, { opacity: 0, scale: 2.4, duration: 0.9, ease: 'power2.out' }, validated + 0.2);
	}

	const repaint: Repaint = () => drawn.forEach(({ thread, state }) => setThread(thread, 0, state.b));
	tl.data = { repaint };
	return tl;
}

/**
 * Masked line reveal of a display heading, split on real font metrics once the web
 * fonts are ready (autoSplit re-measures on resize). Words stay real text (aria none),
 * French non-breaking spaces are kept. Returns a cleanup for the pending font wait.
 */
export function revealTitle(heading: HTMLElement, context: gsap.Context, trigger: Element, start: string): () => void {
	let cancelled = false;
	gsap.set(heading, { autoAlpha: 0 });
	const ready = document.fonts ? document.fonts.ready : Promise.resolve();

	ready.then(() => {
		if (cancelled) return;
		context.add(() => {
			gsap.set(heading, { autoAlpha: 1 });
			SplitText.create(heading, {
				type: 'lines',
				mask: 'lines',
				aria: 'none',
				autoSplit: true,
				reduceWhiteSpace: false,
				onSplit: (split: SplitText) => {
					// Tight display leading would clip accents and descenders inside the masks.
					gsap.set(split.masks, {
						paddingTop: '0.06em',
						marginTop: '-0.06em',
						paddingBottom: '0.16em',
						marginBottom: '-0.16em',
					});
					return gsap.from(split.lines, {
						yPercent: 112,
						duration: 1.15,
						ease: EASE,
						stagger: 0.08,
						scrollTrigger: { trigger, start },
					});
				},
			});
		});
	});

	return () => {
		cancelled = true;
	};
}

/** Scrubbing text reveal of the closing sentence: words light up in reading order. */
export function scrubWords(line: Element): gsap.core.Tween {
	return gsap.fromTo(
		line.querySelectorAll('[data-closing-word]'),
		{ opacity: 0.16 },
		{
			opacity: 1,
			ease: 'none',
			duration: 1,
			stagger: 0.14,
			scrollTrigger: { trigger: line, start: 'top 88%', end: 'bottom 58%', scrub: 0.5 },
		},
	);
}
